import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { dashboardAPI, patientsAPI } from '../lib/api';
import { useAuth } from '../lib/auth';
import { cn, formatDate } from '../lib/utils';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Users,
  ClipboardList,
  UserPlus,
  Activity,
  ArrowRight,
  Calendar,
  TrendingUp,
  FileText,
  Sparkles,
  Clock,
  Stethoscope,
  ChevronRight,
  Plus,
  Paperclip,
  Download,
  FileImage,
  File,
} from 'lucide-react';

export default function DashboardPage() {
  const navigate = useNavigate();
  const { user, hasPermission } = useAuth();
  const [stats, setStats] = useState(null);
  const [recentPatients, setRecentPatients] = useState([]);
  const [recentDocuments, setRecentDocuments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, patientsRes, docsRes] = await Promise.all([
          dashboardAPI.getStats(),
          patientsAPI.getAll({ limit: 5 }),
          dashboardAPI.getRecentDocuments(8),
        ]);
        setStats(statsRes.data);
        setRecentPatients(patientsRes.data);
        setRecentDocuments(docsRes.data || []);
      } catch (err) {
        console.error('Failed to fetch dashboard data:', err);
      }
      setLoading(false);
    };

    fetchData();
  }, []);

  const statCards = [
    {
      title: 'Total Patients',
      value: stats?.patients || 0,
      icon: Users,
      trend: '+12%',
      trendUp: true,
    },
    {
      title: 'Appointments',
      value: stats?.appointments || 0,
      icon: Calendar,
      trend: '+5%',
      trendUp: true,
    },
    {
      title: 'Form Templates',
      value: stats?.templates || 0,
      icon: ClipboardList,
      trend: 'Active',
      trendUp: true,
    },
    {
      title: "Today's Activity",
      value: stats?.today_appointments || 0,
      icon: Activity,
      trend: 'Live',
      trendUp: true,
    },
  ];

  const quickActions = [
    { label: 'New Patient', icon: UserPlus, href: '/app/patients/new', permission: 'patients:create' },
    { label: 'Schedule', icon: Calendar, href: '/app/scheduling', permission: 'appointments:read' },
    { label: 'Forms', icon: ClipboardList, href: '/app/forms', permission: 'forms:read' },
    { label: 'Invoices', icon: FileText, href: '/app/invoices', permission: 'invoices:read' },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-primary/30 border-t-primary rounded-full animate-spin" />
          <p className="text-slate-500 text-sm">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fadeIn p-2" data-testid="dashboard">
      {/* Welcome Section */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <p className="text-sm font-medium text-primary mb-1">Welcome back</p>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">
            {user?.full_name?.split(' ')[0]}
          </h1>
          <p className="text-slate-500 mt-2">
            Here's what's happening in your practice today
          </p>
        </div>
        <div className="premium-card px-4 py-2 flex items-center gap-3">
          <div className="w-2 h-2 bg-primary rounded-full animate-pulse" />
          <span className="text-sm text-slate-600">
            {(() => {
              const d = new Date();
              const day = d.getDate().toString().padStart(2, '0');
              const month = d.toLocaleString('en-US', { month: 'short' });
              const year = d.getFullYear();
              const weekday = d.toLocaleString('en-US', { weekday: 'long' });
              return `${weekday}, ${day}/${month}/${year}`;
            })()}
          </span>
        </div>
      </div>

      {/* Stats Grid - Bento Style */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
        {statCards.map((stat, index) => (
          <div 
            key={stat.title} 
            className={cn(
              "premium-card-hover p-6 relative overflow-hidden group",
              index === 0 && "sm:col-span-2 lg:col-span-1"
            )} 
            data-testid={`stat-${index}`}
          >
            {/* Subtle glow effect */}
            <div className="absolute top-0 right-0 w-32 h-32 bg-primary/5 rounded-full blur-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
            
            <div className="relative">
              <div className="flex items-start justify-between mb-4">
                <div className="stat-card-icon">
                  <stat.icon className="h-5 w-5 text-primary" strokeWidth={1.5} />
                </div>
                <Badge className={cn(
                  "text-xs font-medium rounded-full",
                  stat.trendUp ? "bg-emerald-50 text-emerald-600 border border-emerald-200" : "bg-red-50 text-red-600 border border-red-200"
                )}>
                  {stat.trend}
                </Badge>
              </div>
              
              <div>
                <p className="text-4xl font-bold tracking-tight text-slate-900">{stat.value}</p>
                <p className="text-sm text-slate-500 mt-1">{stat.title}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Quick Actions */}
      <div className="premium-card p-6">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h2 className="text-lg font-semibold text-slate-900">Quick Actions</h2>
            <p className="text-sm text-slate-500">Common tasks at your fingertips</p>
          </div>
        </div>
        <div className="flex flex-wrap gap-3">
          {quickActions
            .filter(action => hasPermission(action.permission))
            .map((action) => (
              <Button
                key={action.label}
                className={cn(
                  "rounded-full gap-2 px-5 transition-all duration-300",
                  action.label === 'New Patient' 
                    ? "bg-primary text-white hover:bg-primary/90 btn-primary-glow"
                    : "bg-slate-50 text-slate-700 hover:bg-slate-100 border border-slate-200"
                )}
                onClick={() => navigate(action.href)}
                data-testid={`quick-action-${action.label.toLowerCase().replace(' ', '-')}`}
              >
                <action.icon className="h-4 w-4" strokeWidth={1.5} />
                {action.label}
              </Button>
            ))}
        </div>
      </div>

      {/* Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Patients */}
        <div className="premium-card overflow-hidden">
          <div className="p-6 border-b border-slate-100">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-slate-900">Recent Patients</h2>
                <p className="text-sm text-slate-500">Latest patient records</p>
              </div>
              <Button 
                variant="ghost" 
                size="sm" 
                className="gap-1 text-slate-500 hover:text-primary rounded-full"
                onClick={() => navigate('/app/patients')}
                data-testid="view-all-patients"
              >
                View all
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
          
          <ScrollArea className="h-[320px]">
            <div className="p-4 space-y-2">
              {recentPatients.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <div className="w-16 h-16 rounded-2xl bg-slate-50 flex items-center justify-center mb-4">
                    <Users className="h-8 w-8 text-slate-400" strokeWidth={1.5} />
                  </div>
                  <p className="text-slate-500">No patients yet</p>
                  <Button 
                    className="mt-4 rounded-full btn-primary-glow gap-2"
                    onClick={() => navigate('/app/patients/new')}
                  >
                    <Plus className="h-4 w-4" />
                    Add First Patient
                  </Button>
                </div>
              ) : (
                recentPatients.map((patient, index) => (
                  <div
                    key={patient.id}
                    className={cn(
                      "flex items-center gap-4 p-4 rounded-xl cursor-pointer transition-all duration-200",
                      "hover:bg-slate-50 group"
                    )}
                    onClick={() => navigate(`/app/patients/${patient.id}`)}
                    data-testid={`patient-${index}`}
                    style={{ animationDelay: `${index * 50}ms` }}
                  >
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary/10 to-primary/5 flex items-center justify-center border border-primary/10 group-hover:border-primary/20 transition-colors">
                      <span className="font-semibold text-sm text-primary">
                        {patient.first_name?.[0]}{patient.last_name?.[0]}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-slate-900 truncate">
                        {patient.first_name} {patient.last_name}
                      </p>
                      <p className="text-sm text-slate-500">
                        ID: {patient.patient_id}
                      </p>
                    </div>
                    <ChevronRight className="h-5 w-5 text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                ))
              )}
            </div>
          </ScrollArea>
        </div>

        {/* Activity / Today's Schedule */}
        <div className="premium-card overflow-hidden">
          <div className="p-6 border-b border-slate-100">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-slate-900">Today's Schedule</h2>
                <p className="text-sm text-slate-500">Upcoming appointments</p>
              </div>
              <Button 
                variant="ghost" 
                size="sm" 
                className="gap-1 text-slate-500 hover:text-primary rounded-full"
                onClick={() => navigate('/app/scheduling')}
                data-testid="view-schedule"
              >
                Full schedule
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
          
          <div className="p-4">
            {stats?.today_appointments === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <div className="w-16 h-16 rounded-2xl bg-slate-50 flex items-center justify-center mb-4">
                  <Calendar className="h-8 w-8 text-slate-400" strokeWidth={1.5} />
                </div>
                <p className="text-slate-500">No appointments today</p>
                <Button 
                  className="mt-4 rounded-full btn-primary-glow gap-2"
                  onClick={() => navigate('/app/scheduling')}
                >
                  <Plus className="h-4 w-4" />
                  Schedule Appointment
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center gap-4 p-4 rounded-xl bg-primary/5 border border-primary/10">
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                    <Stethoscope className="h-5 w-5 text-primary" strokeWidth={1.5} />
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-slate-900">{stats?.today_appointments} Appointments</p>
                    <p className="text-sm text-slate-500">Scheduled for today</p>
                  </div>
                  <Button 
                    variant="ghost"
                    size="sm"
                    className="rounded-full text-primary hover:bg-primary/10"
                    onClick={() => navigate('/app/scheduling')}
                  >
                    View
                  </Button>
                </div>
                
                <div className="grid grid-cols-2 gap-3">
                  <div className="p-4 rounded-xl bg-slate-50 border border-slate-100">
                    <p className="text-2xl font-bold text-slate-900">{stats?.templates || 0}</p>
                    <p className="text-xs text-slate-500 mt-1">Active Forms</p>
                  </div>
                  <div className="p-4 rounded-xl bg-slate-50 border border-slate-100">
                    <p className="text-2xl font-bold text-slate-900">{stats?.appointments || 0}</p>
                    <p className="text-xs text-slate-500 mt-1">Total Appointments</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Recent Documents Section */}
      <div className="premium-card overflow-hidden">
        <div className="p-6 border-b border-slate-100">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                <Paperclip className="h-5 w-5 text-primary" strokeWidth={1.5} />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-slate-900">Recent Documents</h2>
                <p className="text-sm text-slate-500">Latest uploaded files across all patients</p>
              </div>
            </div>
          </div>
        </div>
        
        <div className="p-4">
          {recentDocuments.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <div className="w-16 h-16 rounded-2xl bg-slate-50 flex items-center justify-center mb-4">
                <FileText className="h-8 w-8 text-slate-400" strokeWidth={1.5} />
              </div>
              <p className="text-slate-500">No documents uploaded yet</p>
              <p className="text-sm text-slate-400 mt-1">Upload files from patient profiles</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
              {recentDocuments.map((doc, index) => {
                const isImage = doc.content_type?.startsWith('image/');
                const isPDF = doc.content_type === 'application/pdf';
                const FileIcon = isImage ? FileImage : isPDF ? FileText : File;
                
                return (
                  <div
                    key={doc.id}
                    className="p-4 rounded-xl bg-slate-50 border border-slate-100 hover:border-slate-200 hover:bg-slate-100/50 transition-all group cursor-pointer"
                    onClick={() => navigate(`/app/patients/${doc.patient_id}`)}
                    data-testid={`recent-doc-${index}`}
                    style={{ animationDelay: `${index * 30}ms` }}
                  >
                    <div className="flex items-start gap-3">
                      <div className={cn(
                        "w-10 h-10 rounded-lg flex items-center justify-center shrink-0",
                        isImage ? "bg-blue-50" : isPDF ? "bg-red-50" : "bg-slate-100"
                      )}>
                        <FileIcon className={cn(
                          "h-5 w-5",
                          isImage ? "text-blue-500" : isPDF ? "text-red-500" : "text-slate-500"
                        )} strokeWidth={1.5} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm text-slate-900 truncate" title={doc.filename}>
                          {doc.filename}
                        </p>
                        <p className="text-xs text-slate-500 truncate mt-0.5">
                          {doc.patient_name}
                        </p>
                        <p className="text-xs text-slate-400 mt-1">
                          {formatDate(doc.uploaded_at)}
                        </p>
                      </div>
                    </div>
                    <div className="mt-3 flex items-center justify-between">
                      <span className="text-xs text-slate-400 truncate" title={doc.file_type}>
                        {doc.file_type || 'Document'}
                      </span>
                      <button
                        className="opacity-0 group-hover:opacity-100 transition-opacity p-1.5 rounded-lg hover:bg-slate-200"
                        onClick={(e) => {
                          e.stopPropagation();
                          const token = localStorage.getItem('emr_auth_token');
                          window.open(`${process.env.REACT_APP_BACKEND_URL}/api/files/${doc.id}?token=${token}`, '_blank');
                        }}
                        title="Download file"
                      >
                        <Download className="h-4 w-4 text-slate-500" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
